import React from 'react';
/* eslint-disable react/no-did-mount-set-state */
import bindAll from 'lodash.bindall';
import SerialMonitorComponent from '../components/serial-monitor/serial-monitor.jsx';
import hardwareUploader from '../lib/hardware-uploader';

class SerialMonitor extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleToggle',
            'handleConnect',
            'handleDisconnect',
            'handleInputChange',
            'handleBaudRateChange',
            'handleKeyPress',
            'handleSend',
            'handleClear',
            'setTerminalRef',
            'handleToggleTimestamp',
            'handleToggleAutoScroll',
            'handleTogglePause',
            'handleSearchChange',
            'handleLineEndingChange',
            'handleCopyAll',
            'handleSaveLog',
            'onGlobalConnect',
            'onGlobalDisconnect',
            'onGlobalUploadStart',
            'onGlobalUploadEnd',
            'onGlobalData',
            'onGlobalError',
            'onGlobalBaudRateChanging',
            'onGlobalBaudRateChanged'
        ]);

        this.state = {
            expanded: false,
            connected: hardwareUploader.isConnected,
            error: null,
            messages: [],
            inputText: '',
            baudRate: hardwareUploader.baudRate.toString(),
            showTimestamp: false,
            autoScroll: true,
            isPaused: false,
            searchQuery: '',
            lineEnding: 'nl_cr',
            viewMode: 'console',
            rxCount: 0,
            txCount: 0,
            isChangingBaudRate: hardwareUploader._isChangingBaudRate || false
        };

        this.terminal = null;
        this.bufferedMessages = [];
        this.incomingBuffer = '';
        this.messageIdCounter = 0;
    }

    componentDidMount () {
        hardwareUploader.addEventListener('connect', this.onGlobalConnect);
        hardwareUploader.addEventListener('disconnect', this.onGlobalDisconnect);
        hardwareUploader.addEventListener('uploadStart', this.onGlobalUploadStart);
        hardwareUploader.addEventListener('uploadEnd', this.onGlobalUploadEnd);
        hardwareUploader.addEventListener('data', this.onGlobalData);
        hardwareUploader.addEventListener('error', this.onGlobalError);
        hardwareUploader.addEventListener('baudRateChanging', this.onGlobalBaudRateChanging);
        hardwareUploader.addEventListener('baudRateChanged', this.onGlobalBaudRateChanged);
        
        // Sync state just in case it connected before mount
        this.setState({
            connected: hardwareUploader.isConnected,
            baudRate: hardwareUploader.baudRate.toString()
        });
    }

    componentWillUnmount () {
        hardwareUploader.removeEventListener('connect', this.onGlobalConnect);
        hardwareUploader.removeEventListener('disconnect', this.onGlobalDisconnect);
        hardwareUploader.removeEventListener('uploadStart', this.onGlobalUploadStart);
        hardwareUploader.removeEventListener('uploadEnd', this.onGlobalUploadEnd);
        hardwareUploader.removeEventListener('data', this.onGlobalData);
        hardwareUploader.removeEventListener('error', this.onGlobalError);
        hardwareUploader.removeEventListener('baudRateChanging', this.onGlobalBaudRateChanging);
        hardwareUploader.removeEventListener('baudRateChanged', this.onGlobalBaudRateChanged);
    }

    // ── Global Events ───────────────────────────────────────────────

    onGlobalConnect () {
        this.setState({
            connected: true,
            error: null,
            baudRate: hardwareUploader.baudRate.toString()
        });
        this.pushSystemMessage(`Connected to device at ${hardwareUploader.baudRate} baud`);
    }

    onGlobalDisconnect () {
        this.setState({connected: false});
        this.pushSystemMessage('Disconnected.');
    }

    onGlobalUploadStart () {
        this.pushSystemMessage('Upload started. Pausing serial monitor...');
    }

    onGlobalUploadEnd () {
        this.pushSystemMessage('Upload finished. Resuming serial monitor...');
    }

    onGlobalData (e) {
        this.processIncomingData(e.detail);
    }

    onGlobalError (e) {
        this.pushSystemMessage(`Read error: ${e.detail}`, 'error');
    }

    onGlobalBaudRateChanging () {
        this.setState({isChangingBaudRate: true});
    }

    onGlobalBaudRateChanged () {
        this.setState({
            isChangingBaudRate: false,
            baudRate: hardwareUploader.baudRate.toString()
        });
    }

    // ── Message Management ──────────────────────────────────────────

    addMessages (newMessages) {
        if (this.state.isPaused) {
            this.bufferedMessages.push(...newMessages);
            return;
        }

        this.setState(prevState => {
            const messages = [...prevState.messages];
            
            for (const msg of newMessages) {
                const lastMsg = messages[messages.length - 1];
                if (lastMsg && lastMsg.type === msg.type && lastMsg.content === msg.content) {
                    lastMsg.count += 1;
                    lastMsg.timestamp = msg.timestamp;
                } else {
                    messages.push(msg);
                }
            }
            
            if (messages.length > 100000) {
                return {messages: messages.slice(messages.length - 100000)};
            }
            return {messages};
        }, () => {
            if (this.state.autoScroll && this.terminal) {
                this.terminal.scrollToRow(this.state.messages.length - 1);
            }
        });
    }

    pushSystemMessage (content, type = 'system') {
        this.addMessages([{
            id: this.messageIdCounter++,
            type: type,
            content: content,
            timestamp: Date.now(),
            count: 1
        }]);
    }

    processIncomingData (value) {
        this.incomingBuffer += value;
        const lines = this.incomingBuffer.split('\n');
        this.incomingBuffer = lines.pop(); // keep remainder

        if (lines.length === 0) return;

        const newMessages = lines.map(line => {
            const content = line.replace(/\r$/, '');
            return {
                id: this.messageIdCounter++,
                type: 'incoming',
                content,
                timestamp: Date.now(),
                count: 1
            };
        });

        this.addMessages(newMessages);
        this.setState(prev => ({rxCount: prev.rxCount + newMessages.length}));
    }

    // ── UI Actions ──────────────────────────────────────────────────

    setTerminalRef (ref) {
        this.terminal = ref;
    }

    handleToggle () {
        this.setState({expanded: !this.state.expanded});
    }

    async handleConnect () {
        try {
            await hardwareUploader.connect(this.state.baudRate);
        } catch (e) {
            this.setState({error: e.message});
        }
    }

    async handleDisconnect () {
        await hardwareUploader.disconnect();
    }

    handleInputChange (e) {
        this.setState({inputText: e.target.value});
    }

    handleBaudRateChange (e) {
        const newBaud = e.target.value;
        this.setState({baudRate: newBaud});
        
        hardwareUploader.changeBaudRate(newBaud).catch(err => {
            this.pushSystemMessage(`Error changing baud rate: ${err.message}`, 'error');
            this.setState({baudRate: hardwareUploader.baudRate.toString()});
        });
    }

    handleLineEndingChange (e) {
        this.setState({lineEnding: e.target.value});
    }

    handleSearchChange (e) {
        this.setState({searchQuery: e.target.value});
    }

    handleToggleTimestamp () {
        this.setState(prev => ({showTimestamp: !prev.showTimestamp}));
    }

    handleToggleAutoScroll () {
        this.setState(prev => ({autoScroll: !prev.autoScroll}));
    }

    handleTogglePause () {
        this.setState(prev => {
            const willPause = !prev.isPaused;
            if (!willPause && this.bufferedMessages.length > 0) {
                const msgs = this.bufferedMessages;
                this.bufferedMessages = [];
                setTimeout(() => this.addMessages(msgs), 0);
            }
            return {isPaused: willPause};
        });
    }

    handleToggleViewMode (mode) {
        this.setState({viewMode: mode});
    }

    handleKeyPress (e) {
        if (e.key === 'Enter') {
            this.handleSend();
        }
    }

    async handleSend () {
        if (!this.state.connected || !this.state.inputText) return;

        try {
            let text = this.state.inputText;
            if (this.state.lineEnding === 'nl') text += '\n';
            else if (this.state.lineEnding === 'cr') text += '\r';
            else if (this.state.lineEnding === 'nl_cr') text += '\r\n';

            await hardwareUploader.write(text);
            
            this.addMessages([{
                id: this.messageIdCounter++,
                type: 'outgoing',
                content: this.state.inputText,
                timestamp: Date.now(),
                count: 1
            }]);

            this.setState(prev => ({inputText: '', txCount: prev.txCount + 1}));
        } catch (e) {
            this.pushSystemMessage(`Write error: ${e.message}`, 'error');
        }
    }

    handleClear () {
        this.bufferedMessages = [];
        this.incomingBuffer = '';
        this.setState({messages: [], rxCount: 0, txCount: 0});
    }

    handleCopyAll () {
        const text = this.state.messages.map(m => {
            let prefix = '';
            if (m.type === 'incoming') prefix = '<- ';
            else if (m.type === 'outgoing') prefix = '-> ';
            else if (m.type === 'system') prefix = '[sys] ';
            else if (m.type === 'warning') prefix = '[warn] ';
            else if (m.type === 'error') prefix = '[err] ';
            return `${prefix}${m.content}`;
        }).join('\n');
        
        navigator.clipboard.writeText(text).catch(e => console.error('Failed to copy', e));
    }

    handleSaveLog () {
        const text = this.state.messages.map(m => {
            const time = new Date(m.timestamp).toISOString();
            let prefix = '';
            if (m.type === 'incoming') prefix = '<- ';
            else if (m.type === 'outgoing') prefix = '-> ';
            else if (m.type === 'system') prefix = '[sys] ';
            else if (m.type === 'warning') prefix = '[warn] ';
            else if (m.type === 'error') prefix = '[err] ';
            return `[${time}] ${prefix}${m.content}`;
        }).join('\n');

        const blob = new Blob([text], {type: 'text/plain'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `serial_log_${Date.now()}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    render () {
        return (
            <SerialMonitorComponent
                baudRate={this.state.baudRate}
                connected={this.state.connected}
                error={this.state.error}
                expanded={this.state.expanded}
                inputText={this.state.inputText}
                messages={this.state.messages}
                showTimestamp={this.state.showTimestamp}
                autoScroll={this.state.autoScroll}
                isPaused={this.state.isPaused}
                searchQuery={this.state.searchQuery}
                lineEnding={this.state.lineEnding}
                viewMode={this.state.viewMode}
                rxCount={this.state.rxCount}
                txCount={this.state.txCount}
                isChangingBaudRate={this.state.isChangingBaudRate}
                onBaudRateChange={this.handleBaudRateChange}
                onLineEndingChange={this.handleLineEndingChange}
                onSearchChange={this.handleSearchChange}
                onToggleTimestamp={this.handleToggleTimestamp}
                onToggleAutoScroll={this.handleToggleAutoScroll}
                onTogglePause={this.handleTogglePause}
                onToggleViewMode={this.handleToggleViewMode}
                onCopyAll={this.handleCopyAll}
                onSaveLog={this.handleSaveLog}
                onConnect={this.handleConnect}
                onDisconnect={this.handleDisconnect}
                onInputChange={this.handleInputChange}
                onKeyPress={this.handleKeyPress}
                onSend={this.handleSend}
                onClear={this.handleClear}
                onToggle={this.handleToggle}
                terminalRef={this.setTerminalRef}
            />
        );
    }
}

export default SerialMonitor;
